/**
 * An immutable type which acts a provider of `esri.symbols.Symbol`s to use in display operations.
 *
 * The following Field model attribute sets are located under the static `fields` attribute.
 * * color
 * * fillColor
 * * lineWidth
 * * markerSize
 * * markerStyle
 * * lineStyle
 * * fillStyle
 * @module app/models/map/SymbolFactory
 */
import _ from "underscore";
import Backbone from "backbone";
import isMobile from "core/is-mobile";

function combineAlpha(color, alpha) {
  var Color = window.require("dojo/_base/Color");
  var result = new Color(color);

  if (_.isNumber(alpha)) {
    var rgba = result.toRgba();

    rgba[3] = alpha;
    result = new Color(rgba);
  }

  return result;
}

/**
 * COpies a color over but maintains teh alpha value
 * @param  {Color} color1 The color to keep the original alpha value from
 * @param  {Color} color2 The color to transfer into color1
 * @return {String}  A string of a color in rgba format with color1 having color2's values but keeping it's original alpha value
 */
function maintainAlpha(color1, color2) {
  var Color = window.require("dojo/_base/Color");
  var colorResult;
  var color1Object = convertToColorObject(color1);
  var color2Object = convertToColorObject(color2);
  //get the alpha from color 1
  var color1Alpha = color1Object.toRgba()[3];

  colorResult = new Color([
    color2Object.toRgba()[0],
    color2Object.toRgba()[1],
    color2Object.toRgba()[2],
    color1Alpha,
  ]);

  return colorResult.toString();
}

/**
 * Creates a new color object
 * @param  {} color The color to keep the original alpha value from
 * @return {Object}  an object of type dojo/_base/color
 */
function convertToColorObject(color) {
  var Color = window.require("dojo/_base/Color");
  var colorObject;

  if (_.isString(color)) {
    colorObject = Color.fromRgb(color);
  } else if (_.isArray(color)) {
    if (color[3]) {
      colorObject = new Color([color[0], color[1], color[2], color[3]]);
    } else {
      colorObject = new Color(color[0], color[1], color[2]);
    }
  } else {
    return color;
  }

  return colorObject;
}

var SymbolFactory;

var SimpleMarkerSymbolConstants = {
  STYLE_CIRCLE: "circle",
  STYLE_SQUARE: "square",
  STYLE_CROSS: "cross",
  STYLE_X: "x",
  STYLE_DIAMOND: "diamond",
  STYLE_PATH: "path",
  STYLE_TARGET: "target",
};

var SimpleLineSymbolConstants = {
  STYLE_SOLID: "solid",
  STYLE_DASH: "dash",
  STYLE_DOT: "dot",
  STYLE_DASHDOT: "dashdot",
  STYLE_DASHDOTDOT: "longdashdotdot",
  STYLE_NULL: "none",
  STYLE_SHORTDASH: "shortdash",
  STYLE_SHORTDOT: "shortdot",
  STYLE_SHORTDASHDOT: "shortdashdot",
  STYLE_SHORTDASHDOTDOT: "shortdashdotdot",
  STYLE_LONGDASH: "longdash",
  STYLE_LONGDASHDOT: "longdashdot",
};

var SimpleFillSymbolConstants = {
  STYLE_SOLID: "solid",
  STYLE_NULL: "none",
  STYLE_HORIZONTAL: "horizontal",
  STYLE_VERTICAL: "vertical",
  STYLE_FORWARD_DIAGONAL: "forwarddiagonal",
  STYLE_BACKWARD_DIAGONAL: "backwarddiagonal",
  STYLE_CROSS: "cross",
  STYLE_DIAGONAL_CROSS: "diagonalcross",
  STYLE_FORWARDDIAGONAL: "forwarddiagonal",
  STYLE_BACKWARDDIAGONAL: "backwarddiagonal",
  STYLE_DIAGONALCROSS: "diagonalcross",
};

export default SymbolFactory = Backbone.Model.extend(
  {
    defaults: function () {
      return {
        color: "red",
        linkColors: false,
        fillColor: null,
        lineWidth: 8,
        markerStyle: SimpleMarkerSymbolConstants.STYLE_CIRCLE,
        markerSize: 20,
        lineStyle: SimpleLineSymbolConstants.STYLE_SOLID,
        fillStyle: SimpleFillSymbolConstants.STYLE_SOLID,
        fillAlpha: null,
        alpha: null,
      };
    },

    /**
     * Constructor.
     * @param {object} options The options hash.
     * @param {dojo/_base/Color} options.color:'red' The outline color.
     * @param {dojo/_base/Color} [options.fillColor] The fill color. If not specified, `options.color` is used.
     * @param {Number} [options.alpha] [description] A number between 0 and 1, inclusive, to use as the transparency value for `options.color`, if not already specified. `0` is transparent, `1` is opaque.
     * @param {Number} [options.fillAlpha] The alpha value to apply to the fill color.
     * @param {Number} options.lineWidth:8 The desired width of line symbols, in pixels.
     * @param {Number} options.markerSize:20 The desired size of point symbols, in pixels.
     * @param {string} options.markerStyle:esri.symbols.SimpleMarkerSymbol.STYLE_CIRCLE The desired style of point symbols. One of the constants in `esri.symbols.SimpleMarkerSymbol`.
     * @param {string} options.lineStyle:esri.symbols.SimpleLineSymbol.STYLE_SOLID The desired style of line symbols. One of the constants in `esri.symbols.SimpleLineSymbol`.
     * @param {string} options.fillStyle:esri.symbols.SimpleFillSymbol.STYLE_SOLID The desired style of fill symbols. One of the constants in `esri.symbols.SimpleFillSymbol`.
     * @return {Function} A function accepting a single `string` argument, which should be one of `'multipoint'`, `'point'`, `'polyline'`, `'polygon'`. Returns the symbol appropriate for the given input. Also has member functions `fill`, `line`, and `marker`. The member function `toJSON` serializes the original options to a JSON format.
     */
    initialize: function (attributes, options) {
      Backbone.Model.prototype.initialize.apply(this, arguments);

      var attrs = this.attributes;

      attrs.color = combineAlpha(attrs.color, attrs.alpha);
      var fillColor = attrs.fillColor || attrs.color;

      attrs.fillColor = combineAlpha(fillColor, attrs.fillAlpha);

      var linkingColors = false;

      this.on(
        "change:color",
        function () {
          if (this.get("linkColors") && !linkingColors) {
            var color = this.get("color"),
              fillColor = this.get("fillColor"),
              colorObj = convertToColorObject(color),
              fillColorObj = convertToColorObject(fillColor);

            if (
              fillColorObj.toRgb().toString() != colorObj.toRgb().toString()
            ) {
              linkingColors = true;
              this.set("fillColor", maintainAlpha(fillColor, color));
              linkingColors = false;
            }
          }
        },
        this
      );

      this.on(
        "change:fillColor",
        function () {
          if (this.get("linkColors") && !linkingColors) {
            var color = this.get("color"),
              fillColor = this.get("fillColor"),
              colorObj = convertToColorObject(color),
              fillColorObj = convertToColorObject(fillColor);

            if (
              fillColorObj.toRgb().toString() != colorObj.toRgb().toString()
            ) {
              linkingColors = true;
              this.set("color", maintainAlpha(color, fillColor));
              linkingColors = false;
            }
          }
        },
        this
      );

      return attrs;
    },

    fill: function () {
      var SimpleFillSymbol = window.require("esri/symbols/SimpleFillSymbol");

      return new SimpleFillSymbol(
        this.get("fillStyle"),
        this.line(),
        convertToColorObject(this.get("fillColor"))
      );
    },

    line: function () {
      var SimpleLineSymbol = window.require("esri/symbols/SimpleLineSymbol");

      return new SimpleLineSymbol(
        this.get("lineStyle"),
        convertToColorObject(this.get("color")),
        this.get("lineWidth")
      );
    },

    marker: function () {
      var SimpleMarkerSymbol = window.require(
        "esri/symbols/SimpleMarkerSymbol"
      );

      return new SimpleMarkerSymbol(
        this.get("markerStyle"),
        this.get("markerSize"),
        this.line(),
        convertToColorObject(this.get("fillColor"))
      );
    },

    byType: function (type) {
      var result;

      switch (type) {
        case "multipoint":
        case "point":
          result = this.marker();
          break;

        case "polyline":
          result = this.line();
          break;

        case "polygon":
          result = this.fill();
          break;
      }

      return result;
    },

    toJSON: function () {
      var json = Backbone.Model.prototype.toJSON.apply(this, arguments);

      delete json.fillAlpha;
      delete json.alpha; //these are both already encoded in the colors

      return json;
    },
  },
  {
    /**
     * Returns the default `SymbolFactory`.
     * @return {SymbolFactory} The default symbol factory.
     */
    default: function () {
      return new SymbolFactory({
        fillAlpha: 0.6,
        lineWidth: 3,
      });
    },

    /**
     * Returns the `SymbolFactory` to use for highlighting.
     * @return {SymbolFactory} The highlighted symbol factory.
     */
    highlight: function () {
      var markerSize = isMobile ? 35 : 10;

      return new SymbolFactory({
        color: "#FF00CC",
        markerSize: markerSize,
        fillAlpha: 0.6,
        lineWidth: 6,
      });
    },
  }
);

Object.defineProperty(SymbolFactory, "fields", {
  get: function () {
    return {
      color: {
        id: "color",
        name: "color",
        label: "Color",
        editor: "color",
      },
      linkColors: {
        id: "linkColors",
        name: "linkColors",
        label: "Link Colors",
        editor: "checkbox",
      },
      fillColor: {
        id: "fillColor",
        name: "fillColor",
        label: "Fill Color",
        editor: "color",
      },
      lineWidth: {
        id: "lineWidth",
        name: "lineWidth",
        label: "Line Width",
        editor: "number",
      },
      markerSize: {
        id: "markerSize",
        name: "markerSize",
        label: "Marker Size",
        editor: "number",
      },
      markerStyle: {
        id: "markerStyle",
        name: "markerStyle",
        label: "Marker Style",
        values: [
          {
            value: SimpleMarkerSymbolConstants.STYLE_CIRCLE,
            text: "Circle",
          },
          {
            value: SimpleMarkerSymbolConstants.STYLE_CROSS,
            text: "Cross",
          },
          {
            value: SimpleMarkerSymbolConstants.STYLE_DIAMOND,
            text: "Diamond",
          },
          {
            value: SimpleMarkerSymbolConstants.STYLE_PATH,
            text: "Path",
          },
          {
            value: SimpleMarkerSymbolConstants.STYLE_SQUARE,
            text: "Square",
          },
          {
            value: SimpleMarkerSymbolConstants.STYLE_X,
            text: "X",
          },
        ],
      },
      lineStyle: {
        id: "lineStyle",
        name: "lineStyle",
        label: "Line Style",
        values: [
          {
            value: SimpleLineSymbolConstants.STYLE_DASH,
            text: "Dash",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_DASHDOT,
            text: "Dash Dot",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_DASHDOTDOT,
            text: "Dash Dot Dot",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_DOT,
            text: "Dot",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_LONGDASH,
            text: "Long Dash",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_LONGDASHDOT,
            text: "Long Dash Dot",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_NULL,
            text: "None",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_SHORTDASH,
            text: "Short Dash",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_SHORTDASHDOT,
            text: "Short Dash Dot",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_SHORTDASHDOTDOT,
            text: "Short Dash Dot Dot",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_SHORTDOT,
            text: "Short Dot",
          },
          {
            value: SimpleLineSymbolConstants.STYLE_SOLID,
            text: "Solid",
          },
        ],
      },
      fillStyle: {
        id: "fillStyle",
        name: "fillStyle",
        label: "Fill Style",
        values: [
          {
            value: SimpleFillSymbolConstants.STYLE_BACKWARD_DIAGONAL,
            text: "Backward Diagonal",
          },
          {
            value: SimpleFillSymbolConstants.STYLE_CROSS,
            text: "Cross",
          },
          {
            value: SimpleFillSymbolConstants.STYLE_DIAGONAL_CROSS,
            text: "Diagonal Cross",
          },
          {
            value: SimpleFillSymbolConstants.STYLE_FORWARD_DIAGONAL,
            text: "Forward Diagonal",
          },
          {
            value: SimpleFillSymbolConstants.STYLE_HORIZONTAL,
            text: "Horizontal",
          },
          {
            value: SimpleFillSymbolConstants.STYLE_NULL,
            text: "None",
          },
          {
            value: SimpleFillSymbolConstants.STYLE_SOLID,
            text: "Solid",
          },
          {
            value: SimpleFillSymbolConstants.STYLE_VERTICAL,
            text: "Vertical",
          },
        ],
      },
    };
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/SymbolFactory.js
// module id = 69
// module chunks = 1